import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useForm, Controller, FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { Customer } from '@/services/customers/customers.service';
import { isValidCNPJ } from '@/utils/validators';
import { formatCNPJ, stripCNPJ } from '@/utils/cnpj';

type FormMode = 'create' | 'edit';

const customerSchema = z.object({
  legalName: z
    .string()
    .min(2, 'Razão social deve ter no mínimo 2 caracteres')
    .max(255, 'Razão social deve ter no máximo 255 caracteres'),
  cnpj: z
    .string()
    .min(1, 'CNPJ é obrigatório')
    .refine(val => {
      const cleaned = stripCNPJ(val);
      return /^[A-Z0-9]{12}\d{2}$/.test(cleaned);
    }, 'CNPJ deve ter 12 caracteres alfanuméricos e 2 dígitos verificadores numéricos')
    .refine(val => isValidCNPJ(val), 'CNPJ inválido'),
});

type CustomerFormData = z.infer<typeof customerSchema>;

interface CustomerFormDialogProps {
  open: boolean;
  mode: FormMode;
  customer?: Customer;
  onClose: () => void;
  onSubmit: (data: { legalName: string; cnpj: string }) => Promise<void>;
}

/**
 * Unified dialog for creating and editing customers
 *
 * Features:
 * - Create mode: All fields required
 * - Edit mode: Pre-filled with customer data
 * - Form validation with Zod
 * - CNPJ formatting and validation
 * - Loading states
 * - Error handling
 */
export const CustomerFormDialog: React.FC<CustomerFormDialogProps> = ({
  open,
  mode,
  customer,
  onClose,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEditMode = mode === 'edit';

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerFormData>({
    resolver: async data => {
      try {
        const validatedData = customerSchema.parse(data);
        return {
          values: validatedData,
          errors: {},
        };
      } catch (error) {
        if (error instanceof z.ZodError) {
          const fieldErrors: FieldErrors<CustomerFormData> = {};
          error.issues.forEach(issue => {
            const fieldName = issue.path[0] as keyof CustomerFormData;
            if (fieldName && !fieldErrors[fieldName]) {
              fieldErrors[fieldName] = {
                type: issue.code,
                message: issue.message,
              };
            }
          });
          return {
            values: {},
            errors: fieldErrors,
          };
        }
        return {
          values: {},
          errors: {},
        };
      }
    },
    mode: 'onSubmit',
    defaultValues: {
      legalName: '',
      cnpj: '',
    },
  });

  // Reset form when dialog opens or customer changes
  useEffect(() => {
    if (open) {
      if (isEditMode && customer) {
        // Pre-fill with customer data in edit mode
        reset({
          legalName: customer.legalName,
          cnpj: customer.cnpj,
        });
      } else {
        // Reset to defaults in create mode
        reset({
          legalName: '',
          cnpj: '',
        });
      }
      setSubmitError(null);
    }
  }, [open, isEditMode, customer, reset]);

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setSubmitError(null);
      onClose();
    }
  };

  const handleFormSubmit = async (data: CustomerFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit({
        legalName: data.legalName,
        cnpj: stripCNPJ(data.cnpj),
      });
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError(
          isEditMode
            ? 'Erro ao atualizar empresa. Tente novamente.'
            : 'Erro ao criar empresa. Tente novamente.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    return isEditMode ? 'Editar Empresa' : 'Nova Empresa';
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) {
      return isEditMode ? 'Salvando...' : 'Criando...';
    }
    return isEditMode ? 'Salvar Alterações' : 'Criar Empresa';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          {getTitle()}
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Legal Name Field */}
            <Controller
              name="legalName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Razão Social"
                  fullWidth
                  error={!!errors.legalName}
                  helperText={errors.legalName?.message}
                  disabled={isSubmitting}
                  autoFocus
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            {/* CNPJ Field */}
            <Controller
              name="cnpj"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="CNPJ"
                  fullWidth
                  error={!!errors.cnpj}
                  helperText={errors.cnpj?.message || 'AA.AAA.AAA/AAAA-00'}
                  disabled={isSubmitting || isEditMode} // Disable CNPJ in edit mode
                  onChange={e => {
                    const formatted = formatCNPJ(e.target.value);
                    field.onChange(formatted);
                  }}
                  slotProps={{
                    htmlInput: { autoCapitalize: 'characters', maxLength: 18 },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting} variant="outlined" color="inherit">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {getSubmitButtonText()}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
